import { Icons } from "@stacc/flow-ui-components";
import { NormalInfoWrapper } from "../../../tasks/styles";
import { OfferGivenWrapper } from "../../loan/styles";
import { RowsWrapper } from "../../styles";
import { OfferType, type CommonData } from "../../../../types/common";
import { formatAmount } from "../../../../utils/helperFunctions";

interface OfferDetailsProps {
  data: CommonData
  status: string
}
interface Detail {
  label: string,
  value: any,
}

function getIndexOfLastRow(details: Detail[]) {
  let lastValidRow = -1;
  let secondLastValidRow = -1;
  let validCount = 0;

  for (let i = 0; i < details.length; i++) {
    if (details[i].value != null) {
      secondLastValidRow = lastValidRow;
      lastValidRow = i;
      validCount++;
    }
  }

  return validCount % 2 === 0
    ? [secondLastValidRow, lastValidRow]
    : [lastValidRow];
}

export const OfferRow = ({ data, status }: OfferDetailsProps) => {
  const finalLoanAmount = data?.offer?.finalLoanAmount || 0;
  const process = data.agentApiApplication?.process;
  const country = data?.country;

  // Offer valid until
  const { offer, integrations } = data || {};
  const { 
    validUntil, insuranceCost, insuranceSelected, interestRate, maxLoanAmount,
    effectiveInterestRate, cashPayoutAmount, refinanceAmount, topUpAmount,
    maxCashPayoutAmount, monthlyCost
  } = offer || {};
  const { selectedOffer } = integrations?.LeaBankWeb?.startInformation || {};

  const offerExpirationDate = validUntil;
  const offerExpired = offerExpirationDate ? new Date(offerExpirationDate) < new Date() : false;

  const offerValidUntilText =  offerExpired ? "Offer expired" : "Offer valid until";
  const creditLimit = process === "card-se" && data?.offer?.creditLimit ? formatAmount(data.offer.creditLimit, country) : null;
  const annualFee = data?.offer?.annualFee ? formatAmount(data.offer.annualFee, country) : null;


  const formattedInterestRate = interestRate ? `${interestRate}%` : null
  const formattedEffectiveInterestRate = effectiveInterestRate ? `${effectiveInterestRate}%` : null

  const terms = data?.offer?.terms;

  const offerGivenIcon = finalLoanAmount ? (
    <Icons.SuccessCircle />
  ) : status === "active" ? (
    <Icons.ActiveCircle />
  ) : (
    <Icons.AbortedCircle />
  );

  const { tinkRequired_rule, tinkRequiredCoApp_rule } = data?.integrations?.statistica?.SE_External?.variables || {};

  const offerDetails = [
    { label: offerValidUntilText, value: offerExpirationDate ? Intl.DateTimeFormat("en-GB").format(new Date(offerExpirationDate)).replace(/\//g, ".") : null},
    { label: "Monthly cost", value: monthlyCost ? formatAmount(monthlyCost, country) : null },
    { label: "Loan amount", value: finalLoanAmount ? formatAmount(finalLoanAmount, country) : null },
    { label: "Nominal interest rate", value: formattedInterestRate },
    { label: "Max loan amount", value: maxLoanAmount ? formatAmount(maxLoanAmount, country) : null },
    { label: "Effective interest rate", value: formattedEffectiveInterestRate },
    { label: "Cash payout amount", value: cashPayoutAmount ? formatAmount(cashPayoutAmount, country) : null },
    { label: "Terms", value: terms },
    { label: "Max cash approved", value: maxCashPayoutAmount ? formatAmount(maxCashPayoutAmount, country) : null },
    { label: "Approved internal refinance amount", value: topUpAmount ? formatAmount(topUpAmount, country) : null },
    { label: "Approved external refinance amount", value: refinanceAmount ? formatAmount(refinanceAmount, country) : null },
    { label: "Insurance selected", value: insuranceSelected ? "Yes" : "No" },
    { label: "Tink required", value: Number(tinkRequired_rule) === 1 ? "Yes" : "No" },
    { label: "Insurance cost", value: insuranceCost ? formatAmount(insuranceCost, country) : null }, // corrected "Insurance const" to "Insurance cost"
    {
      label: "Offer given", value: <div style={{
        position: "relative",
      }}><OfferGivenWrapper>{offerGivenIcon}</OfferGivenWrapper></div>
    },
    ...data?.agentApiApplication?.coapplicant ? [
      { label: "Coapplicant - Tink required", value: Number(tinkRequiredCoApp_rule) === 1 ? "Yes" : "No" },
    ] : [],
    { label: "Credit limit", value: creditLimit },
    { label: "Annual fee", value: annualFee },
    { label: "Offer selected", value: selectedOffer === OfferType.COUNTER ? "Counter offer" : selectedOffer === OfferType.ORIGINAL ? "Original offer" : "Not selected" },
  ]

  const lastIndexes = getIndexOfLastRow(offerDetails)

  const isEmpty = offerDetails.every(detail => detail.value === null || detail.value === undefined);
  if (isEmpty) return null

  return (
    <RowsWrapper className="Offer information">
      <NormalInfoWrapper $header>Offer Details</NormalInfoWrapper>
      {offerDetails.map((detail, index) => {
        if (detail.value === null || detail.value === undefined) return
        return (
          <NormalInfoWrapper key={`Offer row - ${detail.label}`} $isLast={lastIndexes.includes(index)}>
            <div>{detail.label}</div>
            <div>{detail.value}</div>
          </NormalInfoWrapper>
        )
      })}
    </RowsWrapper>
  )
}